import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, X, Save, Search, Filter, Plus } from "lucide-react";

/**
 * Colored badge for a weekly check-in score (0-100).
 * - Verde (emerald)  ≥ 80  → "Alta"
 * - Âmbar (amber)    60–79 → "Média"
 * - Vermelho (red)   < 60  → "Baixa"
 * - Cinza dashed           → sem score
 * Exposed data-testid: `checkin-score-badge-{id}` with variant class hint
 * for automated tests (`score-tier-{high|mid|low|na}`).
 */
function ScoreBadge({ value, id }) {
  const n = Number(value);
  const has = value !== null && value !== undefined && Number.isFinite(n);
  let tier = "na", cls = "border-dashed border-muted-foreground/40 text-muted-foreground bg-transparent", label = "—";
  if (has) {
    if (n >= 80) { tier = "high"; cls = "border-emerald-500/40 bg-emerald-500/10 text-emerald-800"; }
    else if (n >= 60) { tier = "mid"; cls = "border-amber-500/40 bg-amber-500/10 text-amber-900"; }
    else { tier = "low"; cls = "border-destructive/40 bg-destructive/10 text-destructive"; }
    label = Math.round(n);
  }
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[2.75rem] px-2 py-0.5 rounded-sm border text-sm font-semibold tabular-nums ${cls}`}
      data-testid={`checkin-score-badge-${id}`}
      data-score-tier={tier}
      title={has ? `Score ${n} / 100` : "Sem score registrado"}
    >
      {label}
    </span>
  );
}

export default function AdminCheckinsPage() {
  const [items, setItems] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [protoFilter, setProtoFilter] = useState("");
  const [editing, setEditing] = useState(null); // {..., answers}
  const [creating, setCreating] = useState(null); // { user_id, protocol_id, answers, side_effects }
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // full checkin obj

  const load = async () => {
    try {
      const [ci, pr, ps] = await Promise.all([
        api.get("/admin/checkins"),
        api.get("/admin/protocols"),
        api.get("/admin/patients"),
      ]);
      setItems(ci.data);
      setProtocols(pr.data);
      setPatients(ps.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { load(); }, []);

  const protocolsById = useMemo(() => Object.fromEntries(protocols.map((p) => [p.id, p])), [protocols]);
  const patientsById = useMemo(() => Object.fromEntries(patients.map((p) => [p.id, p])), [patients]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((c) => {
      if (protoFilter && (c.protocol_id || "") !== protoFilter) return false;
      if (!ql) return true;
      const name = (c.patient_name || patientsById[c.user_id]?.name || "").toLowerCase();
      const email = (c.patient_email || patientsById[c.user_id]?.email || "").toLowerCase();
      return name.includes(ql) || email.includes(ql);
    });
  }, [items, q, protoFilter, patientsById]);

  const openEdit = (c) => {
    // build answers dict: start from stored answers, else legacy fields
    const legacyKeys = ["energy", "sleep", "intestine", "adherence"];
    const answers = { ...(c.answers || {}) };
    for (const k of legacyKeys) if (c[k] !== undefined && answers[k] === undefined) answers[k] = c[k];
    setEditing({ ...c, answers, side_effects: c.side_effects || "" });
  };
  const openNew = () => {
    if (patients.length === 0) { toast.error("Cadastre pacientes antes."); return; }
    const first = patients[0];
    const protoId = first.protocol_id || protocols[0]?.id || "";
    const proto = protocolsById[protoId];
    const answers = {};
    for (const q of (proto?.checkin_questions || [])) answers[q.key] = 5;
    setCreating({ user_id: first.id, protocol_id: protoId, answers, side_effects: "" });
  };
  const close = () => { setEditing(null); setCreating(null); };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        answers: editing.answers || {},
        side_effects: editing.side_effects || "",
      };
      // keep legacy fields in sync when present
      for (const k of ["energy", "sleep", "intestine", "adherence", "hair_loss"]) {
        if (editing.answers && editing.answers[k] !== undefined) payload[k] = Number(editing.answers[k]);
      }
      await api.patch(`/admin/checkins/${editing.id}`, payload);
      toast.success("Check-in atualizado.");
      close();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  const saveNew = async () => {
    if (!creating) return;
    if (!creating.user_id) { toast.error("Selecione um paciente."); return; }
    setSaving(true);
    try {
      const payload = {
        user_id: creating.user_id,
        protocol_id: creating.protocol_id || null,
        answers: creating.answers || {},
        side_effects: creating.side_effects || "",
      };
      for (const k of ["energy", "sleep", "intestine", "adherence", "hair_loss"]) {
        if (creating.answers && creating.answers[k] !== undefined) payload[k] = Number(creating.answers[k]);
      }
      await api.post("/admin/checkins", payload);
      toast.success("Check-in criado.");
      close();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar.");
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/checkins/${c.id}`);
      toast.success("Check-in removido.");
      setConfirmDelete(null);
      await load();
    } catch (_) {
      toast.error("Erro ao remover.");
    }
  };

  // Build question list for a protocol id (fallback to default).
  // Always ensures the "Queda capilar" (hair_loss) question is included per spec.
  const questionsFor = (pid) => {
    const p = protocolsById[pid];
    const defaults = [
      { key: "energy", label: "Energia", description: "0–10", min: 0, max: 10 },
      { key: "sleep", label: "Sono", description: "0–10", min: 0, max: 10 },
      { key: "intestine", label: "Intestino", description: "0–10", min: 0, max: 10 },
      { key: "adherence", label: "Adesão ao protocolo", description: "0–10", min: 0, max: 10 },
      { key: "hair_loss", label: "Queda capilar", description: "0 = nenhuma · 10 = intensa", min: 0, max: 10 },
    ];
    if (p && (p.checkin_questions || []).length > 0) {
      const existing = new Set((p.checkin_questions || []).map((q) => q.key));
      const missing = defaults.filter((d) => !existing.has(d.key));
      return [...p.checkin_questions, ...missing];
    }
    return defaults;
  };

  const renderEditor = (state, setState, submit, isNew) => {
    const qs = questionsFor(state.protocol_id);
    return (
      <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close} data-testid="checkin-modal">
        <div className="bg-card border border-border rounded-sm p-0 max-w-xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between p-6 border-b border-border">
            <div>
              <div className="overline text-gold mb-1">{isNew ? "Novo check-in" : "Editar check-in"}</div>
              <h3 className="brand-wordmark uppercase text-xl font-bold text-primary">
                {isNew ? "Registrar resposta" : (state.patient_name || "Paciente")}
              </h3>
              {!isNew && state.created_at && (
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(state.created_at).toLocaleString("pt-BR")}
                </div>
              )}
            </div>
            <button onClick={close} className="p-1 hover:bg-muted rounded-sm" data-testid="close-checkin-modal"><X className="w-4 h-4" /></button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {isNew && (
              <>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Paciente</label>
                  <select value={state.user_id} onChange={(e) => {
                    const uid = e.target.value;
                    const p = patientsById[uid];
                    const pid = p?.protocol_id || "";
                    const proto = protocolsById[pid];
                    const answers = {};
                    for (const q of (proto?.checkin_questions || [])) answers[q.key] = 5;
                    setState({ ...state, user_id: uid, protocol_id: pid, answers });
                  }} className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="checkin-patient-select">
                    {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.email}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Protocolo</label>
                  <select value={state.protocol_id || ""} onChange={(e) => {
                    const pid = e.target.value;
                    const proto = protocolsById[pid];
                    const answers = {};
                    for (const q of (proto?.checkin_questions || [])) answers[q.key] = 5;
                    setState({ ...state, protocol_id: pid, answers });
                  }} className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="checkin-protocol-select">
                    <option value="">— (usa perguntas padrão) —</option>
                    {protocols.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </>
            )}

            {qs.map((q) => (
              <div key={q.key}>
                <div className="flex justify-between items-end mb-1">
                  <div>
                    <div className="font-heading font-semibold text-sm">{q.label}</div>
                    {q.description && <div className="text-xs text-muted-foreground">{q.description}</div>}
                  </div>
                  <div className="font-heading text-xl font-bold text-primary" data-testid={`checkin-value-${q.key}`}>
                    {state.answers?.[q.key] ?? 0}
                  </div>
                </div>
                <input type="range" min={q.min ?? 0} max={q.max ?? 10}
                  value={state.answers?.[q.key] ?? 0}
                  onChange={(e) => setState({ ...state, answers: { ...(state.answers || {}), [q.key]: Number(e.target.value) } })}
                  className="w-full accent-primary"
                  data-testid={`checkin-input-${q.key}`} />
              </div>
            ))}

            <div>
              <label className="overline text-muted-foreground mb-1 block">Efeitos colaterais / observações</label>
              <textarea rows={3} value={state.side_effects || ""}
                onChange={(e) => setState({ ...state, side_effects: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="checkin-sideeffects-input" />
            </div>
          </div>

          <div className="border-t border-border p-4 flex gap-2 justify-end">
            <button onClick={close} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-checkin-btn">Cancelar</button>
            <button onClick={submit} disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
              data-testid="save-checkin-btn">
              <Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-checkins-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3">Monitoramento</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Check-ins</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Gestão de respostas de check-ins semanais. Edite valores, adicione uma resposta manual ou remova entradas incorretas.
            As perguntas de cada protocolo são definidas em <b>Protocolos</b>.
          </p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-checkin-btn">
          <Plus className="w-4 h-4" /> Novo check-in
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input placeholder="Buscar paciente…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="checkins-search-input" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select value={protoFilter} onChange={(e) => setProtoFilter(e.target.value)}
            className="px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="checkins-protocol-filter">
            <option value="">Todos protocolos</option>
            {protocols.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} registro{filtered.length === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-4 py-3 overline text-muted-foreground">Paciente</th>
              <th className="px-4 py-3 overline text-muted-foreground">Protocolo</th>
              <th className="px-4 py-3 overline text-muted-foreground">Data</th>
              <th className="px-4 py-3 overline text-muted-foreground">
                <div className="flex items-center gap-2 flex-wrap" data-testid="score-legend">
                  <span>Score</span>
                  <span className="hidden sm:inline-flex items-center gap-1 normal-case tracking-normal">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500/60" title="≥80 · alta" />
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-500/70" title="60–79 · média" />
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-destructive/70" title="<60 · baixa" />
                  </span>
                </div>
              </th>
              <th className="px-4 py-3 overline text-muted-foreground">Energia · Sono · Intestino · Adesão · Queda</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`checkin-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{c.patient_name || patientsById[c.user_id]?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{c.patient_email || patientsById[c.user_id]?.email || ""}</div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{c.protocol_name || protocolsById[c.protocol_id]?.name || "—"}</td>
                <td className="px-4 py-3">{c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : ""}</td>
                <td className="px-4 py-3"><ScoreBadge value={c.score} id={c.id} /></td>
                <td className="px-4 py-3 font-mono text-xs">
                  {(c.answers?.energy ?? c.energy ?? "—")} · {(c.answers?.sleep ?? c.sleep ?? "—")} · {(c.answers?.intestine ?? c.intestine ?? "—")} · {(c.answers?.adherence ?? c.adherence ?? "—")} · {(c.answers?.hair_loss ?? "—")}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => openEdit(c)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid={`edit-checkin-${c.id}`} title="Editar">
                      <Pencil className="w-4 h-4 text-primary" />
                    </button>
                    <button onClick={() => setConfirmDelete(c)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid={`delete-checkin-${c.id}`} title="Excluir">
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">Nenhum check-in encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && renderEditor(editing, setEditing, saveEdit, false)}
      {creating && renderEditor(creating, setCreating, saveNew, true)}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-checkin-modal">
          <div className="bg-card border border-border rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="overline text-destructive mb-1">Confirmar exclusão</div>
                <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Excluir check-in?</h3>
              </div>
              <button onClick={() => setConfirmDelete(null)} className="p-1 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-muted-foreground">
              Tem certeza que deseja excluir este check-in de{" "}
              <b>{confirmDelete.created_at ? new Date(confirmDelete.created_at).toLocaleDateString("pt-BR") : "data desconhecida"}</b>
              {" "}({confirmDelete.patient_name || patientsById[confirmDelete.user_id]?.name || "paciente"})?
              Esta ação não pode ser desfeita.
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                data-testid="cancel-delete-checkin-btn">
                Cancelar
              </button>
              <button onClick={() => remove(confirmDelete)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90 transition-colors"
                data-testid="confirm-delete-checkin-btn">
                <Trash2 className="w-4 h-4" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
